import React, { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import { GridTable, GridTableHeaderItem, Spinner } from '@uikit';

import { useSelfZealyAccountQuery, useZealyParticipantListInfiniteQuery } from '@query';

import { DeserializedZealyRef, ZealyStoryTaskParticipant } from '@api';

import { MsqToken } from '@uikit/icons/MsqToken';

import NextLink from '@shared/common/components/NextLink';
import { useAuth } from '@shared/common/providers/AuthProvider';

import { roadToTgeQuests } from '../hardcodedStories/roadToTgeQuests';

export const getParticipantPosition = (
  participant: ZealyStoryTaskParticipant | DeserializedZealyRef,
  index: number,
) => {
  let position = `#${index + 1}`;

  if (participant.type === 'ZEALY_REF') {
    position = participant.attributes.rank ? `#${participant.attributes.rank}` : '';
  }

  return position;
};

export const ZealyParticipantXp = ({
  participant,
  className,
}: {
  participant: ZealyStoryTaskParticipant | DeserializedZealyRef;
  className?: string;
}) => {
  return (
    <span
      className={classNames(
        'inline-flex justify-center items-center text-xs rounded-full px-2 py-1 min-h-[1.5rem] bg-primary-100',
        className,
      )}
    >
      {participant.attributes.xp} XP
    </span>
  );
};

export const ZealyParticipantReward = ({
  participant,
  currency,
  className,
}: {
  participant: ZealyStoryTaskParticipant | DeserializedZealyRef;
  currency?: string;
  className?: string;
}) => {
  return (
    <span
      className={classNames(
        'inline-flex justify-center items-center text-xs rounded-full gap-1 px-2 py-1 min-h-[1.5rem] bg-secondary text-neutral-50',
        className,
      )}
    >
      <MsqToken className="icon-2xs" />
      {Math.trunc(+(participant.attributes.reward || 0))} {currency || ''}
    </span>
  );
};

const tableHeader: GridTableHeaderItem<
  ZealyStoryTaskParticipant | DeserializedZealyRef,
  { rewardCurrency: string } | undefined
>[] = [
  {
    label: 'Position',
    name: 'position',
    bodyCellClassName: 'max-sm:hidden',
    render: ({ rowIndex, item }) => {
      return getParticipantPosition(item, rowIndex);
    },
  },
  {
    label: 'User',
    name: 'user',
    bodyCellClassName: 'flex sm:items-center gap-2 max-sm:w-full',
    render: ({ item, rowIndex, meta }) => {
      const position = getParticipantPosition(item, rowIndex);

      return (
        <>
          <span className="sm:hidden">{position}</span>

          <ImageProxy
            alt="Avatar"
            src={item.attributes.icon}
            width={22}
            height={22}
            className="flex-shrink-0 rounded-full w-6 h-6 object-cover"
          />

          <div className="flex gap-2 flex-wrap overflow-hidden">
            <span title={item.attributes.address} className="w-full flex text-sm text-neutral-500">
              <span className="truncate max-w-[2.875rem]">
                {item.attributes.address?.slice(0, -4)}
              </span>
              <span>{item.attributes.address?.slice(-4)}</span>
            </span>

            <ZealyParticipantXp className="sm:hidden" participant={item} />
            <ZealyParticipantReward
              className="sm:hidden"
              participant={item}
              currency={meta?.rewardCurrency}
            />
          </div>
        </>
      );
    },
  },
  {
    label: 'Xp',
    name: 'xp',
    bodyCellClassName: 'max-sm:hidden text-center whitespace-nowrap',
    render: ({ item }) => {
      return <ZealyParticipantXp participant={item} />;
    },
  },
  {
    label: 'Reward',
    name: 'reward',
    bodyCellClassName: 'max-sm:hidden text-right whitespace-nowrap',
    render: ({ item, meta }) => {
      return <ZealyParticipantReward participant={item} currency={meta?.rewardCurrency} />;
    },
  },
];

export const useZealyLeaderboardInfiniteListParticipants = () => {
  const { isAuth } = useAuth();
  const { data: selfZealyAccount } = useSelfZealyAccountQuery({
    enabled: isAuth,
  });
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } =
    useZealyParticipantListInfiniteQuery();
  const { ref } = useInView({
    onChange: (inView) => inView && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });
  const participants = useMemo(() => {
    const allParticipants: (DeserializedZealyRef | ZealyStoryTaskParticipant)[] =
      data?.pages.flatMap((page) => page.participants) || [];

    if (selfZealyAccount) {
      // NOTE: push at the end to not break position number of the other participants,
      // because we are using row index to calculate it
      allParticipants.push(selfZealyAccount.account);
    }

    return allParticipants;
  }, [data, selfZealyAccount]);

  return {
    participants,
    data,
    hasNextPage,
    isLoading,
    ref,
  };
};

export const ZealyInfiniteLeaderboard = ({ className }: { className?: string }) => {
  const { data, isLoading, hasNextPage, ref, participants } =
    useZealyLeaderboardInfiniteListParticipants();

  return (
    <GridTable
      header={tableHeader}
      className={classNames('overflow-y-auto flex flex-col gap-2', className)}
      headRowClassName="!hidden"
      bodyRowClassName={({ item }) =>
        item.type === 'ZEALY_REF' ? '-order-1 bg-primary-50 sticky top-0' : undefined
      }
      meta={data?.pages[0]}
      rowClassName="gap-2 p-2 rounded-lg border border-primary-100 flex items-start flex-wrap sm:grid sm:gap-1.5 sm:min-w-min sm:grid-cols-[70px_minmax(60px,1fr)_min(100px,_22%)_100px] sm:flex-shrink-0 sm:h-10 sm:py-0 sm:items-center"
      data={participants}
    >
      {!isLoading && !participants?.length && (
        <p className="m-auto text-center text-neutral-200 text-sm">Leaderboard is Empty</p>
      )}

      {(isLoading || hasNextPage) && (
        // Key is added to trigger in view cb again if component is already in view
        <Spinner ref={ref} key={data?.pages.length} className="m-auto" size="md" />
      )}
    </GridTable>
  );
};

const ZealyLeaderboardStory = () => {
  return (
    <>
      <main className="v2-container store-container-pt flex-grow">
        <ImageProxy
          alt="Road to TGE Banner"
          src="/stories/road-to-tge-banner.png"
          className="w-full h-auto mb-5 max-sm:hidden 3xl:mb-10"
          sizes="100vw"
          width={1742}
          height={296}
          priority
        />

        <ImageProxy
          alt="Road to TGE Banner"
          src="/stories/road-to-tge-banner-mobile.png"
          className="w-full h-auto mb-5 sm:hidden"
          sizes="100vw"
          width={278}
          height={106}
          priority
        />

        <div className="rounded-2xl flex border items-center border-primary-200 bg-primary-50 w-full p-4 mb-5 max-xl:flex-col gap-4 sm:p-8  ">
          <div className="flex-1">
            <span className="block text-secondary-700 font-semibold text-lg md:text-2xl xl:t-h3 mb-4">
              In this Campaign, Everyone is a Winner!
            </span>

            <ul className="list-decimal md:text-lg xl:text-lg font-semibold pl-6">
              <li>
                First 2,000 Participants:{' '}
                <span className="font-light">
                  Earn 1,000,000 SQR tokens based on ranking and points.
                </span>
              </li>
              <li>
                Participants Ranked 2,001 and Beyond:{' '}
                <span className="font-light">Share an additional 1,000,000 SQR tokens pool.</span>
              </li>
              <li>
                Weekly Top Performers and Raffle Winners:{' '}
                <span className="font-light">
                  Receive a part of 500,000 SQR tokens awarded weekly.
                </span>
              </li>
              <li>
                Heads up:{' '}
                <span className="font-light">
                  It can take up to 3 hours for your actions to show on the Leaderboard and
                  Questboard.
                </span>
              </li>
            </ul>
          </div>

          <NextLink
            href={`/stories/${roadToTgeQuests.id}`}
            className="button button-solid-primary button-lg flex-shrink-0"
          >
            Complete Quests for more Points
          </NextLink>
        </div>

        <div className="rounded-sm flex flex-col border border-primary max-h-[20rem] flex-1 basis-0 pt-4">
          <ZealyInfiniteLeaderboard className="px-4 pb-4 sm:px-6 " />
        </div>
      </main>
    </>
  );
};

export default ZealyLeaderboardStory;
